<template>
    <div class="dc">

        <!-- ── Stats row ── -->
        <div class="dc-stats">
            <div class="dc-stat-card">
                <div class="dc-stat-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.22);color:${themeColors.primary};`">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                    </svg>
                </div>
                <p class="dc-stat-lbl">{{ t('walletBalance') }}</p>
                <p class="dc-stat-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(walletBalance) }}</p>
                <span class="dc-stat-sub" :style="`color:${themeColors.primary};`">{{ t('available') }}</span>
            </div>
            <div class="dc-stat-card">
                <div class="dc-stat-icon" style="background:rgba(34,211,238,0.12);border-color:rgba(34,211,238,0.22);color:#22d3ee;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                    </svg>
                </div>
                <p class="dc-stat-lbl">{{ t('totalDeposits') }}</p>
                <p class="dc-stat-val">{{ deposits?.total || 0 }}</p>
                <span class="dc-stat-sub" style="color:#22d3ee;">{{ t('allTime') }}</span>
            </div>
            <div class="dc-stat-card">
                <div class="dc-stat-icon" style="background:rgba(16,185,129,0.12);border-color:rgba(16,185,129,0.22);color:#10b981;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <p class="dc-stat-lbl">{{ t('successfulDeposits') }}</p>
                <p class="dc-stat-val">{{ successfulDeposits }}</p>
                <span class="dc-stat-sub" style="color:#10b981;">{{ t('completed') }}</span>
            </div>
            <div class="dc-stat-card">
                <div class="dc-stat-icon" style="background:rgba(251,191,36,0.12);border-color:rgba(251,191,36,0.22);color:#fbbf24;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <p class="dc-stat-lbl">{{ t('pendingDeposits') }}</p>
                <p class="dc-stat-val">{{ pendingDeposits }}</p>
                <span class="dc-stat-sub" style="color:#fbbf24;">{{ t('processing') }}</span>
            </div>
        </div>

        <!-- ── Main 2-col layout ── -->
        <div class="dc-main">

            <!-- Left: gateway + form -->
            <div class="dc-left">
                <div class="dc-card">
                    <div class="dc-card-head">
                        <div>
                            <h3 class="dc-card-title">{{ t('selectPaymentMethod') }}</h3>
                            <p class="dc-card-sub">{{ t('chooseGateway') }}</p>
                        </div>
                    </div>

                    <!-- Gateway grid -->
                    <div class="dc-gateways">
                        <div
                            v-for="gateway in paymentGateways"
                            :key="gateway.id"
                            class="dc-gateway"
                            :class="selectedGateway?.id === gateway.id ? 'dc-gateway--on' : ''"
                            :style="selectedGateway?.id === gateway.id
                                ? `border-color:rgba(${themeColors.primaryRgb},0.5);background:rgba(${themeColors.primaryRgb},0.06);`
                                : ''"
                            @click="selectGateway(gateway)"
                        >
                            <div class="dc-gateway-icon" :style="`background:linear-gradient(135deg,#7c3aed,${themeColors.primary});`">
                                <span class="dc-gateway-abbr">{{ gateway.name.substring(0, 2).toUpperCase() }}</span>
                            </div>
                            <div class="dc-gateway-info">
                                <p class="dc-gateway-name">{{ gateway.name }}</p>
                                <span class="dc-gateway-fee" :style="`color:${themeColors.primary};background:rgba(${themeColors.primaryRgb},0.12);`">
                                    {{ gateway.fixed_charge > 0 ? currencySymbol + formatNumber(gateway.fixed_charge) + ' + ' : '' }}{{ formatNumber(gateway.percent_charge) }}% {{ t('fee') }}
                                </span>
                            </div>
                            <div v-if="selectedGateway?.id === gateway.id" class="dc-gateway-check" :style="`background:rgba(${themeColors.primaryRgb},0.18);color:${themeColors.primary};`">
                                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Manual credentials -->
                    <div v-if="selectedGateway && selectedGateway.credentials && selectedGateway.type === 'manual'"
                         class="dc-credentials" :style="`border-color:rgba(${themeColors.primaryRgb},0.28);background:rgba(${themeColors.primaryRgb},0.04);`">
                        <p class="dc-cred-title" :style="`color:${themeColors.primary};`">{{ t('paymentCredentials') }}</p>

                        <!-- Credentials image / QR -->
                        <div v-if="selectedGateway.credentials_image" class="dc-cred-img-wrap">
                            <div class="dc-cred-img-box">
                                <img :src="`/assets/files/${selectedGateway.credentials_image}`"
                                     :alt="`${selectedGateway.name} QR`"
                                     class="dc-cred-img"
                                     @error="$event.target.style.display='none'"/>
                            </div>
                        </div>

                        <div class="dc-cred-rows">
                            <div v-for="(value, key) in selectedGateway.credentials" :key="key" class="dc-cred-row">
                                <div class="dc-cred-row-info">
                                    <p class="dc-cred-row-key">{{ formatFieldName(key) }}</p>
                                    <p class="dc-cred-row-val">{{ value }}</p>
                                </div>
                                <button class="dc-copy-btn" :style="`background:rgba(${themeColors.primaryRgb},0.14);color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.28);`"
                                        @click="copyToClipboard(value)" :aria-label="`${t('copy')} ${formatFieldName(key)}`">
                                    <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- TRON permanent wallet -->
                    <div v-if="selectedGateway && selectedGateway.type === 'automatic' && (selectedGateway.slug === 'tron' || selectedGateway.slug === 'tron-usdt' || selectedGateway.slug === 'tron-network')"
                         class="dc-tron">
                        <!-- Header -->
                        <div class="dc-tron-head">
                            <div class="dc-tron-head-icon">
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" style="color:#60a5fa">
                                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 class="dc-tron-title">{{ t('yourPermanentTronWallet') || 'TRON Deposit Wallet (USDT · TRC20)' }}</h3>
                                <div class="dc-tron-badges">
                                    <span class="dc-tron-badge dc-tron-badge--green">TRC20 Network</span>
                                    <span class="dc-tron-badge dc-tron-badge--blue">USDT Only</span>
                                </div>
                            </div>
                        </div>

                        <div class="dc-tron-notice">
                            <p class="dc-tron-notice-txt">{{ t('tronWalletInstructions') || 'Send USDT (TRC20) to this wallet anytime. Deposits credited in 1–2 minutes.' }}</p>
                            <div class="dc-tron-warn">
                                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20" style="color:#fbbf24;flex-shrink:0">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                                </svg>
                                <p><strong style="color:#fde68a">Warning: TRC20 Only</strong> — Send only TRC20 USDT. Other tokens or networks will be lost forever.</p>
                            </div>
                        </div>

                        <!-- Address + QR grid -->
                        <div class="dc-tron-grid">
                            <div class="dc-tron-addr-box">
                                <div class="dc-tron-addr-head">
                                    <span class="dc-tron-addr-lbl">Tron Address (TRC20 USDT ONLY)</span>
                                    <span class="dc-tron-active-badge">Active</span>
                                </div>
                                <code class="dc-tron-addr">{{ $page.props.auth.user.tron_wallet_address || 'Loading...' }}</code>
                                <div class="dc-tron-btns">
                                    <button type="button"
                                            @click="copyToClipboard($page.props.auth.user.tron_wallet_address, 'Wallet address')"
                                            class="dc-tron-btn-copy"
                                            :style="`background:rgba(${themeColors.primaryRgb},0.14);color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.35);`">
                                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                        </svg>
                                        Copy Address
                                    </button>
                                </div>
                            </div>

                            <div class="dc-tron-qr-box">
                                <p class="dc-tron-qr-lbl">Scan QR Code</p>
                                <div ref="qrContainer" class="dc-tron-qr-wrap">
                                    <div class="dc-tron-qr-inner">
                                        <div ref="qrCodeWrapper">
                                            <QRCodeVue3
                                                v-if="$page.props.auth.user.tron_wallet_address"
                                                :value="$page.props.auth.user.tron_wallet_address"
                                                :size="160"
                                                :dotsOptions="{ type: 'rounded', color: '#0f172a' }"
                                                :cornersSquareOptions="{ type: 'extra-rounded', color: '#0f172a' }"
                                                :cornersDotOptions="{ type: 'dot', color: '#0f172a' }"
                                                :backgroundOptions="{ color: '#ffffff' }"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <button type="button" @click="downloadQRCode('png')" class="dc-tron-dl-btn">
                                    <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                    </svg>
                                    Save QR Code
                                </button>
                            </div>
                        </div>

                        <!-- Feature pills -->
                        <div class="dc-tron-features">
                            <div class="dc-tron-feat dc-tron-feat--green">
                                <div class="dc-tron-feat-ic" style="background:rgba(16,185,129,0.18);color:#10b981;">
                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                                </div>
                                <div><p class="dc-tron-feat-title" style="color:#10b981">Instant Detection</p><p class="dc-tron-feat-sub">Auto-credited in 1–2 min</p></div>
                            </div>
                            <div class="dc-tron-feat dc-tron-feat--blue">
                                <div class="dc-tron-feat-ic" style="background:rgba(96,165,250,0.18);color:#60a5fa;">
                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                                </div>
                                <div><p class="dc-tron-feat-title" style="color:#60a5fa">Secure & Permanent</p><p class="dc-tron-feat-sub">Your exclusive address</p></div>
                            </div>
                            <div class="dc-tron-feat dc-tron-feat--purple">
                                <div class="dc-tron-feat-ic" :style="`background:rgba(${themeColors.primaryRgb},0.18);color:${themeColors.primary};`">
                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20"><path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd"/></svg>
                                </div>
                                <div><p class="dc-tron-feat-title" :style="`color:${themeColors.primary}`">No Limits</p><p class="dc-tron-feat-sub">Any amount accepted</p></div>
                            </div>
                            <div class="dc-tron-feat dc-tron-feat--amber">
                                <div class="dc-tron-feat-ic" style="background:rgba(251,191,36,0.18);color:#fbbf24;">
                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/></svg>
                                </div>
                                <div><p class="dc-tron-feat-title" style="color:#fbbf24">24/7 Available</p><p class="dc-tron-feat-sub">Deposit anytime</p></div>
                            </div>
                        </div>

                        <!-- Ready notice -->
                        <div class="dc-tron-ready">
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20" style="color:#10b981;flex-shrink:0;margin-top:1px">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                            </svg>
                            <div>
                                <p class="dc-tron-ready-title">{{ t('walletReadyToReceive') || 'Your Wallet is Ready!' }}</p>
                                <p class="dc-tron-ready-sub">{{ t('sendAnytimeMessage') || 'Send USDT (TRC-20) to your wallet address above at any time. Deposits are automatically detected and credited within 1–2 minutes.' }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Form section -->
                    <div v-if="selectedGateway" class="dc-form-wrap">

                        <!-- NowPayments crypto picker -->
                        <div v-if="selectedGateway.type === 'automatic' && selectedGateway.slug === 'nowpayments'" class="dc-field">
                            <label class="dc-field-lbl">{{ t('selectCryptocurrency') }}</label>
                            <div class="dc-crypto-grid">
                                <div v-for="crypto in availableCryptos" :key="crypto.code"
                                     class="dc-crypto"
                                     :class="selectedCryptoCurrency === crypto.code ? 'dc-crypto--on' : ''"
                                     :style="selectedCryptoCurrency === crypto.code ? `border-color:rgba(${themeColors.primaryRgb},0.5);background:rgba(${themeColors.primaryRgb},0.08);` : ''"
                                     @click="selectedCryptoCurrency = crypto.code">
                                    <div class="dc-crypto-sym" :style="selectedCryptoCurrency === crypto.code ? `background:${themeColors.primary};color:#0f172a;` : ''">{{ crypto.symbol }}</div>
                                    <span class="dc-crypto-code">{{ crypto.code }}</span>
                                </div>
                            </div>
                        </div>

                    <!-- BSC USDT BEP-20 WalletConnect section -->
                    <div v-if="selectedGateway && selectedGateway.type === 'bsc' && selectedGateway.slug === 'bsc-usdt'"
                         class="dc-bsc">
                        <BscUsdtPayment
                            :theme-colors="themeColors"
                            :wallet-addresses="props.bscWallets"
                            @depositSuccess="onBscDepositSuccess"
                        />
                    </div>

                        <!-- Amount input (hide for TRON and BSC) -->
                        <div v-if="!(selectedGateway.type === 'automatic' && (selectedGateway.slug === 'tron' || selectedGateway.slug === 'tron-usdt' || selectedGateway.slug === 'tron-network')) && selectedGateway.type !== 'bsc'" class="dc-field">
                            <label class="dc-field-lbl">{{ t('depositAmount') }} ({{ selectedGateway.currency }})</label>
                            <input
                                v-model.number="depositForm.amount"
                                type="number"
                                :min="selectedGateway.min_amount"
                                :max="selectedGateway.max_amount"
                                step="0.0001"
                                class="dc-input"
                                :placeholder="t('enterAmount')"
                                required
                            />
                            <p class="dc-field-hint">
                                {{ t('min') }}: {{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(selectedGateway.min_amount) }}
                                — {{ t('max') }}: {{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(selectedGateway.max_amount) }}
                            </p>
                        </div>

                        <!-- Dynamic params -->
                        <div v-if="selectedGateway.parameters && selectedGateway.parameters.length > 0" class="dc-params">
                            <p class="dc-params-title">{{ t('paymentInformation') }}</p>
                            <div v-for="param in selectedGateway.parameters" :key="param.field_name" class="dc-field">
                                <label class="dc-field-lbl">
                                    {{ param.field_label }}
                                    <span v-if="param.field_required" class="dc-required">*</span>
                                </label>
                                <input
                                    v-if="['text','email','tel','number','date','datetime-local'].includes(param.field_type)"
                                    v-model="paymentDetails[param.field_name]"
                                    :type="param.field_type"
                                    :placeholder="param.field_placeholder"
                                    :required="param.field_required"
                                    class="dc-input"
                                />
                                <select
                                    v-else-if="param.field_type === 'select'"
                                    v-model="paymentDetails[param.field_name]"
                                    :required="param.field_required"
                                    class="dc-input dc-select"
                                >
                                    <option value="">{{ param.field_placeholder || t('selectOption') }}</option>
                                    <option v-for="(label, value) in param.field_options" :key="value" :value="value">{{ label }}</option>
                                </select>
                                <input
                                    v-else-if="param.field_type === 'file'"
                                    type="file"
                                    :required="param.field_required"
                                    accept="image/*,.pdf"
                                    class="dc-file-input"
                                    @change="handleFileUpload($event, param.field_name)"
                                />
                            </div>
                        </div>

                        <!-- TRON/BSC handled above; for other gateways show submit -->
                        <button
                            v-if="!(selectedGateway.type === 'automatic' && (selectedGateway.slug === 'tron' || selectedGateway.slug === 'tron-usdt' || selectedGateway.slug === 'tron-network')) && selectedGateway.type !== 'bsc'"
                            type="button"
                            :disabled="!canSubmit || isProcessing"
                            class="dc-submit"
                            :style="canSubmit
                                ? `background:linear-gradient(135deg,#7c3aed,${themeColors.primary});box-shadow:0 4px 18px rgba(${themeColors.primaryRgb},0.35);color:#fff;`
                                : 'background:rgba(255,255,255,0.05);color:rgba(148,163,184,0.4);'"
                            @click="submitDeposit"
                        >
                            <svg v-if="isProcessing" class="dc-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:0.25"/>
                                <path fill="currentColor" style="opacity:0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            {{ isProcessing ? t('processing') + '...' : t('submitDepositRequest') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Right sidebar -->
            <div class="dc-right">

                <!-- Payment summary -->
                <div v-if="selectedGateway && isValidAmount" class="dc-card dc-summary">
                    <h3 class="dc-card-title">{{ t('paymentSummary') }}</h3>
                    <div class="dc-summary-rows">
                        <div class="dc-sum-row">
                            <span class="dc-sum-lbl">{{ t('amount') }}</span>
                            <span class="dc-sum-val">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(depositForm.amount) }}</span>
                        </div>
                        <div v-if="calculatedCharge > 0" class="dc-sum-row">
                            <span class="dc-sum-lbl">{{ t('processingFee') }}</span>
                            <span class="dc-sum-val" style="color:#fbbf24;">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(calculatedCharge) }}</span>
                        </div>
                        <div class="dc-sum-row dc-sum-total">
                            <span class="dc-sum-lbl--b">{{ t('totalPayable') }}</span>
                            <span class="dc-sum-total-val" :style="`color:${themeColors.primary};`">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(totalAmount) }}</span>
                        </div>
                    </div>
                    <div class="dc-receive" :style="`border-color:rgba(${themeColors.primaryRgb},0.22);background:rgba(${themeColors.primaryRgb},0.06);`">
                        <span class="dc-receive-lbl">{{ t('youWillReceive') }}</span>
                        <span class="dc-receive-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(depositForm.amount) }}</span>
                    </div>
                </div>

                <!-- Gateway details -->
                <div v-if="selectedGateway" class="dc-card">
                    <div v-if="selectedGateway.file" class="dc-gw-logo">
                        <img :src="`/assets/files/${selectedGateway.file}`" :alt="selectedGateway.name" class="dc-gw-logo-img" @error="$event.target.style.display='none'"/>
                    </div>
                    <h3 class="dc-card-title dc-center">{{ selectedGateway.name }}</h3>
                    <div v-if="selectedGateway.description" class="dc-gw-desc" v-html="selectedGateway.description"></div>
                    <div class="dc-details-list">
                        <div class="dc-detail-row"><span class="dc-detail-lbl">{{ t('currency') }}</span><span class="dc-detail-val">{{ selectedGateway.currency }}</span></div>
                        <div class="dc-detail-row"><span class="dc-detail-lbl">{{ t('type') }}</span><span class="dc-detail-val" style="text-transform:capitalize;">{{ selectedGateway.type }}</span></div>
                        <div class="dc-detail-row"><span class="dc-detail-lbl">{{ t('processing') }}</span><span class="dc-detail-val" :style="`color:${themeColors.primary};`">{{ t('instantManual') }}</span></div>
                    </div>
                </div>

                <!-- Tips -->
                <div class="dc-card">
                    <h3 class="dc-card-title">{{ t('quickTips') }}</h3>
                    <div class="dc-tips">
                        <div class="dc-tip">
                            <div class="dc-tip-dot" :style="`background:${themeColors.primary};`"></div>
                            <div>
                                <p class="dc-tip-title">{{ t('secureTransactions') }}</p>
                                <p class="dc-tip-sub">{{ t('allDepositsEncrypted') }}</p>
                            </div>
                        </div>
                        <div class="dc-tip">
                            <div class="dc-tip-dot" style="background:#fbbf24;"></div>
                            <div>
                                <p class="dc-tip-title">Processing Time</p>
                                <p class="dc-tip-sub">Manual deposits are reviewed within 24 hours of submission.</p>
                            </div>
                        </div>
                        <div class="dc-tip">
                            <div class="dc-tip-dot" style="background:#60a5fa;"></div>
                            <div>
                                <p class="dc-tip-title">Minimum Amount</p>
                                <p class="dc-tip-sub">Check each gateway's minimum deposit limit before sending.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Deposit history ── -->
        <div class="dc-hist-card">
            <div class="dc-hist-head">
                <div class="dc-hist-head-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.22);color:${themeColors.primary};`">
                    <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                    </svg>
                </div>
                <div>
                    <h3 class="dc-hist-title">{{ t('depositHistory') }}</h3>
                    <p class="dc-hist-sub">{{ t('trackDepositTransactions') }}</p>
                </div>
                <div class="dc-hist-chip" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.2);`">
                    <span :style="`color:${themeColors.primary};`">{{ deposits?.total || 0 }}</span>
                    <span>{{ t('totalDepositsCount') }}</span>
                </div>
            </div>

            <!-- Empty -->
            <div v-if="!deposits?.data || deposits.data.length === 0" class="dc-empty">
                <div class="dc-empty-ic" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.18);color:rgba(${themeColors.primaryRgb},0.6);`">
                    <svg width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                    </svg>
                </div>
                <p class="dc-empty-title">{{ t('noDepositsFound') }}</p>
                <p class="dc-empty-sub">{{ t('makeFirstDeposit') }}</p>
            </div>

            <!-- Mobile rows -->
            <div v-else class="dc-mob">
                <div v-for="d in deposits?.data" :key="d.id" class="dc-row">
                    <div class="dc-row-l">
                        <span class="dc-row-dot" :style="`background:${getStatusBadgeColor(d.status)};box-shadow:0 0 7px ${getStatusBadgeColor(d.status)}55;`"></span>
                        <div>
                            <p class="dc-row-trx">{{ d.trx }}</p>
                            <p class="dc-row-date">{{ formatDate(d.created_at) }}</p>
                        </div>
                    </div>
                    <div class="dc-row-r">
                        <p class="dc-row-amount" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(d.deposit_amount) }}</p>
                        <span class="dc-row-badge" :style="`background:${getStatusBadgeColor(d.status)}1a;border-color:${getStatusBadgeColor(d.status)}35;color:${getStatusBadgeColor(d.status)};`">{{ formatStatus(d.status) }}</span>
                    </div>
                    <div class="dc-row-actions">
                        <button class="dc-view-btn" :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`" @click="showDepositDetails(d)">{{ t('viewDetails') }}</button>
                    </div>
                </div>
            </div>

            <!-- Desktop table -->
            <div v-if="deposits?.data && deposits.data.length > 0" class="dc-tbl-wrap">
                <table class="dc-tbl">
                    <thead>
                        <tr class="dc-tbl-hdr">
                            <th class="dc-th">{{ t('trxId') }}</th>
                            <th class="dc-th">{{ t('amount') }}</th>
                            <th class="dc-th">{{ t('depositAmount') }}</th>
                            <th class="dc-th">{{ t('gateway') }}</th>
                            <th class="dc-th">{{ t('status') }}</th>
                            <th class="dc-th">{{ t('date') }}</th>
                            <th class="dc-th">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="d in deposits?.data" :key="d.id" class="dc-tr">
                            <td class="dc-td"><span class="dc-mono">{{ d.trx }}</span></td>
                            <td class="dc-td">
                                <span class="dc-td-val">{{ formatNumber(d.amount) }} {{ d.currency }}</span>
                                <span v-if="d.charge > 0" class="dc-td-fee">+{{ formatNumber(d.charge) }} {{ t('fee') }}</span>
                            </td>
                            <td class="dc-td">
                                <span class="dc-td-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(d.deposit_amount) }}</span>
                                <span class="dc-td-muted">{{ t('toWallet') }}</span>
                            </td>
                            <td class="dc-td">
                                <span class="dc-td-muted">{{ d.payment_gateway?.name || t('notAvailable') }}</span>
                                <span class="dc-td-fee">{{ d.currency }}</span>
                            </td>
                            <td class="dc-td">
                                <span class="dc-badge" :style="`background:${getStatusBadgeColor(d.status)}1a;border-color:${getStatusBadgeColor(d.status)}35;color:${getStatusBadgeColor(d.status)};`">
                                    <i class="dc-badge-dot" :style="`background:${getStatusBadgeColor(d.status)};`"></i>
                                    {{ formatStatus(d.status) }}
                                </span>
                            </td>
                            <td class="dc-td">
                                <p class="dc-td-date">{{ formatDate(d.created_at) }}</p>
                                <p class="dc-td-time">{{ formatTime(d.created_at) }}</p>
                            </td>
                            <td class="dc-td">
                                <button class="dc-tbl-view" :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`" @click="showDepositDetails(d)">
                                    <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="deposits?.data && deposits.data.length > 0 && deposits.links" class="dc-pagi">
                <span class="dc-pagi-info">{{ t('showingResults', { from: deposits.from||0, to: deposits.to||0, total: deposits.total||0 }) }}</span>
                <div class="dc-pagi-btns">
                    <button v-for="(link, i) in deposits.links" :key="i" :disabled="!link.url"
                        class="dc-pagi-btn"
                        :class="link.active ? 'dc-pagi-btn--on' : !link.url ? 'dc-pagi-btn--off' : ''"
                        :style="link.active ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 12px rgba(${themeColors.primaryRgb},0.3);` : ''"
                        @click="goToPage(link.url)" v-html="link.label"
                    />
                </div>
            </div>
        </div>
    </div>

    <!-- ── Detail Modal ── -->
    <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="showModal" class="dc-overlay" @click.self="closeModal">
            <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
                <div v-if="showModal" class="dc-modal" role="dialog" aria-modal="true">
                    <div class="dc-modal-head">
                        <h3 class="dc-modal-title">{{ t('depositDetails') }}</h3>
                        <button class="dc-modal-close" @click="closeModal">
                            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>

                    <div v-if="selectedDeposit" class="dc-modal-body">

                        <!-- Header card -->
                        <div class="dc-modal-hcard">
                            <div class="dc-modal-hcard-l">
                                <div class="dc-modal-hcard-icon" :style="`background:rgba(${themeColors.primaryRgb},0.15);color:${themeColors.primary};`">
                                    <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                                    </svg>
                                </div>
                                <div>
                                    <p class="dc-modal-plan">{{ selectedDeposit.payment_gateway?.name || t('deposit') }}</p>
                                    <p class="dc-modal-plan-sub">Deposit Request</p>
                                </div>
                            </div>
                            <span class="dc-badge" :style="`background:${getStatusBadgeColor(selectedDeposit.status)}1a;border-color:${getStatusBadgeColor(selectedDeposit.status)}35;color:${getStatusBadgeColor(selectedDeposit.status)};`">
                                <i class="dc-badge-dot dc-badge-dot--pulse" :style="`background:${getStatusBadgeColor(selectedDeposit.status)};`"></i>
                                {{ formatStatus(selectedDeposit.status) }}
                            </span>
                        </div>
                        <div class="dc-modal-trx" :style="`color:${themeColors.primary};`">TXN: {{ selectedDeposit.trx }}</div>

                        <!-- Transaction info -->
                        <div class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('transactionInformation') }}</p>
                            <div class="dc-modal-grid2">
                                <div class="dc-modal-field"><span class="dc-modal-field-lbl">{{ t('transactionId') }}</span><span class="dc-modal-field-val dc-mono" :style="`color:${themeColors.primary};`">{{ selectedDeposit.trx }}</span></div>
                                <div class="dc-modal-field"><span class="dc-modal-field-lbl">{{ t('status') }}</span>
                                    <span class="dc-badge" :style="`background:${getStatusBadgeColor(selectedDeposit.status)}1a;border-color:${getStatusBadgeColor(selectedDeposit.status)}35;color:${getStatusBadgeColor(selectedDeposit.status)};`">{{ formatStatus(selectedDeposit.status) }}</span>
                                </div>
                                <div class="dc-modal-field"><span class="dc-modal-field-lbl">{{ t('paymentGateway') }}</span><span class="dc-modal-field-val">{{ selectedDeposit.payment_gateway?.name || t('notAvailable') }}</span></div>
                                <div class="dc-modal-field"><span class="dc-modal-field-lbl">{{ t('currency') }}</span><span class="dc-modal-field-val">{{ selectedDeposit.currency || 'USD' }}</span></div>
                            </div>
                        </div>

                        <!-- Amount details -->
                        <div class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('amountDetails') }}</p>
                            <div class="dc-modal-amounts">
                                <div class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('requestedAmount') }}</span><span class="dc-modal-amount-val">{{ formatNumber(selectedDeposit.amount) }} {{ selectedDeposit.currency }}</span></div>
                                <div v-if="selectedDeposit.charge > 0" class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('processingFee') }}</span><span class="dc-modal-amount-val" style="color:#fbbf24;">{{ formatNumber(selectedDeposit.charge) }} {{ selectedDeposit.currency }}</span></div>
                                <div class="dc-modal-amount-row dc-modal-amount-total">
                                    <span class="dc-modal-amount-lbl--b">{{ t('totalPaid') }}</span>
                                    <span class="dc-modal-total-val" :style="`color:${themeColors.primary};`">{{ formatNumber(selectedDeposit.final_amount || selectedDeposit.amount) }} {{ selectedDeposit.currency }}</span>
                                </div>
                                <div class="dc-modal-amount-row">
                                    <span class="dc-modal-amount-lbl">{{ t('addedToWallet') }}</span>
                                    <span class="dc-modal-amount-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(selectedDeposit.deposit_amount) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- NowPayments payment link -->
                        <div v-if="selectedDeposit.payment_gateway?.slug === 'nowpayments' && getPaymentUrl(selectedDeposit)" class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ paymentLinkLabel }}</p>
                            <p class="dc-modal-note">Complete the payment on NOWPayments. Open the hosted checkout to pick your currency, view the wallet address, or scan the QR to pay.</p>
                            <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:12px;">
                                <a :href="getPaymentUrl(selectedDeposit)" target="_blank" rel="noopener" class="dc-pay-link" :style="`background:linear-gradient(135deg,#7c3aed,${themeColors.primary});`">
                                    {{ paymentLinkLabel }}
                                    <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" d="M14 3h7v7m0-7L10 14m-1 7H3v-6"/></svg>
                                </a>
                                <button v-if="selectedDeposit.payment_details?.pay_address" type="button" class="dc-copy-addr-btn" @click="copyToClipboard(selectedDeposit.payment_details.pay_address, 'Wallet address')">
                                    Copy wallet address
                                </button>
                            </div>
                        </div>

                        <!-- TRON payment details -->
                        <div v-if="(selectedDeposit.payment_gateway?.slug === 'tron' || selectedDeposit.payment_gateway?.slug === 'tron-usdt') && selectedDeposit.payment_details?.tron_address"
                             class="dc-modal-section" style="border-color:rgba(96,165,250,0.25);background:rgba(96,165,250,0.03);">
                            <p class="dc-modal-section-title" style="color:#60a5fa;">{{ t('tronPaymentDetails') || 'TRON Payment Details' }}</p>
                            <div class="dc-modal-amounts">
                                <div class="dc-tron-addr-modal">
                                    <p class="dc-modal-amount-lbl">{{ t('depositAddress') || 'Deposit Address' }}</p>
                                    <div style="display:flex;align-items:center;gap:8px;margin-top:6px;">
                                        <code style="flex:1;font-size:11.5px;font-family:monospace;color:#fff;background:rgba(0,0,0,0.4);padding:8px 12px;border-radius:8px;word-break:break-all;">{{ selectedDeposit.payment_details.tron_address }}</code>
                                        <button type="button" class="dc-tbl-view" style="flex-shrink:0;" :style="`color:#60a5fa;border-color:rgba(96,165,250,0.3);background:rgba(96,165,250,0.08);`" @click="copyToClipboard(selectedDeposit.payment_details.tron_address, 'TRON address')">
                                            <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                                        </button>
                                    </div>
                                </div>
                                <div class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('amount') }}</span><span class="dc-modal-amount-val">{{ formatNumber(selectedDeposit.payment_details.amount) }} {{ selectedDeposit.payment_details.currency }}</span></div>
                                <div class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('network') }}</span><span class="dc-modal-amount-val" style="text-transform:uppercase;">{{ selectedDeposit.payment_details.network || 'mainnet' }}</span></div>
                            </div>

                            <!-- Verify TRON tx -->
                            <div v-if="!selectedDeposit.transaction_id && selectedDeposit.status === 'pending'" style="margin-top:14px;">
                                <label class="dc-field-lbl">{{ t('enterTxHash') || 'Enter Transaction Hash (Optional)' }}</label>
                                <div style="display:flex;gap:8px;margin-top:6px;">
                                    <input v-model="tronTxHash" type="text" class="dc-input" style="flex:1;" placeholder="Enter your TRON transaction hash"/>
                                    <button type="button" class="dc-submit" style="width:auto;padding:0 16px;font-size:12px;" :disabled="!tronTxHash || tronTxHash.length !== 64 || isVerifyingTron" @click="verifyTronTransaction(selectedDeposit)">
                                        {{ isVerifyingTron ? (t('verifying') || 'Verifying...') : (t('verify') || 'Verify') }}
                                    </button>
                                </div>
                            </div>

                            <div v-if="selectedDeposit.transaction_id" style="margin-top:12px;padding:10px 12px;border-radius:9px;background:rgba(16,185,129,0.08);border:1px solid rgba(16,185,129,0.25);">
                                <p style="font-size:11px;color:#10b981;font-weight:700;margin:0 0 4px;">Transaction Hash</p>
                                <code style="font-size:11px;font-family:monospace;color:#a7f3d0;word-break:break-all;">{{ selectedDeposit.transaction_id }}</code>
                            </div>
                        </div>

                        <!-- Payment details -->
                        <div v-if="selectedDeposit.payment_details && Object.keys(selectedDeposit.payment_details).length > 0" class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('paymentDetails') }}</p>
                            <div class="dc-modal-amounts">
                                <div v-for="(value, key) in selectedDeposit.payment_details" :key="key" class="dc-modal-amount-row">
                                    <span class="dc-modal-amount-lbl" style="text-transform:capitalize;">{{ formatFieldName(key) }}</span>
                                    <span class="dc-modal-amount-val">
                                        <template v-if="value && key === 'file'">
                                            <a :href="`/user/wallet/deposit/${selectedDeposit.id}/view/${key}`" target="_blank" class="dc-pay-link" style="font-size:11px;padding:4px 10px;">{{ t('view') }}</a>
                                            <a :href="`/user/wallet/deposit/${selectedDeposit.id}/download/${key}`" class="dc-pay-link" style="font-size:11px;padding:4px 10px;background:rgba(16,185,129,0.6);">{{ t('download') }}</a>
                                        </template>
                                        <template v-else-if="key === 'payment_url' || key === 'invoice_url'">
                                            <a :href="value" target="_blank" rel="noopener" style="color:#60a5fa;text-decoration:underline;font-size:13px;">{{ paymentLinkLabel }}</a>
                                        </template>
                                        <template v-else>{{ value || t('notAvailable') }}</template>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Timeline -->
                        <div class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('timeline') }}</p>
                            <div class="dc-modal-amounts">
                                <div class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('created') }}</span><span class="dc-modal-amount-val">{{ formatDateTime(selectedDeposit.created_at) }}</span></div>
                                <div v-if="selectedDeposit.approved_at" class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('approved') }}</span><span class="dc-modal-amount-val" :style="`color:${themeColors.primary};`">{{ formatDateTime(selectedDeposit.approved_at) }}</span></div>
                                <div v-if="selectedDeposit.rejected_at" class="dc-modal-amount-row"><span class="dc-modal-amount-lbl">{{ t('rejected') }}</span><span class="dc-modal-amount-val" style="color:#f87171;">{{ formatDateTime(selectedDeposit.rejected_at) }}</span></div>
                            </div>
                        </div>

                        <!-- Admin response -->
                        <div v-if="selectedDeposit.admin_response" class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('adminResponse') }}</p>
                            <p class="dc-modal-note">{{ selectedDeposit.admin_response }}</p>
                        </div>

                        <!-- Payment TXN id -->
                        <div v-if="selectedDeposit.transaction_id && !((selectedDeposit.payment_gateway?.slug === 'tron' || selectedDeposit.payment_gateway?.slug === 'tron-usdt') && selectedDeposit.payment_details?.tron_address)" class="dc-modal-section">
                            <p class="dc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('paymentTransactionId') }}</p>
                            <p class="dc-mono" :style="`color:${themeColors.primary};font-size:13px;word-break:break-all;`">{{ selectedDeposit.transaction_id }}</p>
                        </div>
                    </div>

                    <div class="dc-modal-foot">
                        <button class="dc-close-btn" @click="closeModal">{{ t('close') }}</button>
                    </div>
                </div>
            </Transition>
        </div>
    </Transition>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {router, usePage} from '@inertiajs/vue3';
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';
import QRCodeVue3 from 'qrcode.vue';
import BscUsdtPayment from '@/Components/User/Finance/BscUsdtPayment.vue';

const props = defineProps({
    deposits: {
        type: Object,
        required: true,
        default: () => ({data: [], total: 0, links: []})
    },
    paymentGateways: {
        type: Array,
        required: true,
        default: () => []
    },
    walletBalance: {
        type: [Number, String],
        default: 0
    },
    bscWallets: {
        type: Array,
        default: () => []
    },
});

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

const selectedGateway = ref(null);
const isProcessing = ref(false);
const paymentDetails = ref({});
const showModal = ref(false);
const selectedDeposit = ref(null);
const qrCodeWrapper = ref(null);
const qrContainer = ref(null);

const depositForm = ref({ amount: null, payment_gateway_id: null });
const selectedCryptoCurrency = ref('btc');
const tronTxHash = ref('');
const isVerifyingTron = ref(false);
const availableCryptos = ref([
    { code: 'btc', name: 'Bitcoin', symbol: '₿' },
    { code: 'eth', name: 'Ethereum', symbol: 'Ξ' },
    { code: 'usdttrc20', name: 'Tether TRC20', symbol: '₮' },
    { code: 'usdterc20', name: 'Tether ERC20', symbol: '₮' },
    { code: 'usdtbep20', name: 'Tether BEP20', symbol: '₮' },
    { code: 'usdc', name: 'USD Coin', symbol: '$' },
    { code: 'bnbbsc', name: 'Binance Coin', symbol: 'BNB' },
    { code: 'trx', name: 'Tron', symbol: 'TRX' },
    { code: 'doge', name: 'Dogecoin', symbol: 'Ð' },
    { code: 'ltc', name: 'Litecoin', symbol: 'Ł' },
    { code: 'xrp', name: 'Ripple', symbol: 'XRP' },
    { code: 'ada', name: 'Cardano', symbol: '₳' },
    { code: 'matic', name: 'Polygon', symbol: 'M' },
    { code: 'avax', name: 'Avalanche', symbol: 'A' },
    { code: 'sol', name: 'Solana', symbol: '◎' },
    { code: 'dot', name: 'Polkadot', symbol: 'DOT' },
    { code: 'ton', name: 'Toncoin', symbol: 'TON' },
    { code: 'xmr', name: 'Monero', symbol: 'XMR' }
]);

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const onBscDepositSuccess = ({ trx, txHash }) => {
    showToast('USDT (BEP-20) deposit confirmed! Your balance has been credited.', 'success');
    setTimeout(() => router.reload({ only: ['deposits', 'walletBalance'] }), 1500);
};

const translateOr = (key, fallback) => {
    const value = t(key);
    return value && value !== key ? value : fallback;
};

const primaryColor = computed(() => page.props.primaryColor || '#a75ded');
const currencySymbol = computed(() => page.props.currencySymbol || '$');
const paymentLinkLabel = computed(() => translateOr('paymentLink', 'Open Payment Page'));

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 167, g: 93, b: 237 };
};
const rgbToHex = (r, g, b) => '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    return rgbToHex(Math.min(255, Math.max(0, rgb.r + amount)), Math.min(255, Math.max(0, rgb.g + amount)), Math.min(255, Math.max(0, rgb.b + amount)));
};
const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const isValidAmount = computed(() => {
    if (!selectedGateway.value) return false;
    const amount = parseFloat(depositForm.value.amount);
    if (isNaN(amount) || amount <= 0) return false;
    return amount >= parseFloat(selectedGateway.value.min_amount || 0) && amount <= parseFloat(selectedGateway.value.max_amount || Number.MAX_SAFE_INTEGER);
});

const calculatedCharge = computed(() => {
    if (!selectedGateway.value || !isValidAmount.value) return 0;
    const amount = parseFloat(depositForm.value.amount);
    if (isNaN(amount)) return 0;
    return parseFloat(selectedGateway.value.fixed_charge || 0) + (amount * parseFloat(selectedGateway.value.percent_charge || 0) / 100);
});

const totalAmount = computed(() => {
    if (!isValidAmount.value) return 0;
    return parseFloat(depositForm.value.amount) + calculatedCharge.value;
});

const canSubmit = computed(() => {
    if (!isValidAmount.value || isProcessing.value) return false;
    if (selectedGateway.value?.parameters) {
        for (const field of selectedGateway.value.parameters.filter(p => p.field_required)) {
            const val = paymentDetails.value[field.field_name];
            if (!val || (typeof val === 'string' && val.trim() === '')) return false;
        }
    }
    return true;
});

const successfulDeposits = computed(() => {
    if (!props.deposits?.data || !Array.isArray(props.deposits.data)) return 0;
    return props.deposits.data.filter(d => ['approved', 'completed', 'success'].includes(d.status?.toLowerCase())).length;
});

const pendingDeposits = computed(() => {
    if (!props.deposits?.data || !Array.isArray(props.deposits.data)) return 0;
    return props.deposits.data.filter(d => ['pending', 'processing'].includes(d.status?.toLowerCase())).length;
});

const getGatewayCurrencySymbol = (gateway) => {
    if (!gateway?.currency) return '$';
    const map = { 'USD':'$','EUR':'€','GBP':'£','JPY':'¥','BDT':'৳','BTC':'₿','ETH':'Ξ','INR':'₹','CAD':'C$','AUD':'A$' };
    return map[gateway.currency] || gateway.currency + ' ';
};

const getStatusBadgeColor = (status) => {
    if (!status || typeof status !== 'string') return '#94a3b8';
    switch (status.toLowerCase()) {
        case 'approved': case 'completed': case 'success': return themeColors.value.primary;
        case 'pending': case 'processing': return '#fbbf24';
        case 'rejected': case 'failed': case 'cancelled': return '#ef4444';
        default: return '#94a3b8';
    }
};

const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '0.00';
    const n = parseFloat(num);
    if (isNaN(n)) return '0.00';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(n);
};

const formatCryptoAmount = (num) => {
    if (num === null || num === undefined || num === '') return '0.0000';
    const n = parseFloat(num);
    if (isNaN(n)) return '0.0000';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 12 }).format(n);
};

const formatDate = (d) => {
    if (!d) return t('notAvailable');
    try { const dt = new Date(d); return isNaN(dt) ? t('invalidDate') : dt.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }); } catch { return t('invalidDate'); }
};
const formatTime = (d) => {
    if (!d) return '';
    try { const dt = new Date(d); return isNaN(dt) ? '' : dt.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }); } catch { return ''; }
};
const formatDateTime = (d) => {
    if (!d) return t('notAvailable');
    try { const dt = new Date(d); return isNaN(dt) ? t('invalidDate') : dt.toLocaleString('en-US', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return t('invalidDate'); }
};
const formatStatus = (s) => { if (!s || typeof s !== 'string') return t('unknown'); return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(); };
const formatFieldName = (f) => { if (!f || typeof f !== 'string') return ''; return f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()); };
const getPaymentUrl = (deposit) => {
    if (!deposit?.payment_details) return '';
    return deposit.payment_details.payment_url || deposit.payment_details.invoice_url || deposit.payment_details.invoice_link || '';
};

const selectGateway = (gateway) => {
    if (!gateway?.id) { showToast(t('invalidPaymentGateway'), 'error'); return; }
    selectedGateway.value = gateway;
    depositForm.value.payment_gateway_id = gateway.id;
    depositForm.value.amount = null;
    paymentDetails.value = {};
};

const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showToast(t('fileSizeTooLarge'), 'error'); event.target.value = ''; return; }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) { showToast(t('invalidFileType'), 'error'); event.target.value = ''; return; }
    paymentDetails.value[fieldName] = file;
    showToast(t('fileSelectedSuccessfully'), 'success');
};

const submitDeposit = async () => {
    if (!canSubmit.value) { showToast(t('ensureRequiredFieldsFilled'), 'error'); return; }
    isProcessing.value = true;
    try {
        const formData = new FormData();
        formData.append('payment_gateway_id', depositForm.value.payment_gateway_id);
        formData.append('amount', depositForm.value.amount);
        if (selectedGateway.value?.slug === 'nowpayments') formData.append('selected_currency', selectedCryptoCurrency.value);
        Object.keys(paymentDetails.value).forEach(key => {
            const value = paymentDetails.value[key];
            if (value instanceof File) { formData.append(`payment_details[${key}]`, value); }
            else if (value !== null && value !== undefined && value !== '') {
                const sv = String(value).trim();
                if (sv) formData.append(`payment_details[${key}]`, sv);
            }
        });
        router.post('/user/wallet/deposit', formData, {
            onSuccess: (pg) => { resetForm(); const flash = pg.props.flash; showToast(flash?.success || t('depositRequestSubmitted'), 'success'); },
            onError: (errors) => { showToast(Object.values(errors).flat()[0] || t('depositSubmissionFailed'), 'error'); },
            preserveScroll: true, forceFormData: true
        });
    } catch { showToast(t('depositSubmissionFailed'), 'error'); }
    finally { isProcessing.value = false; }
};

const showDepositDetails = (deposit) => {
    if (!deposit?.id) { showToast(t('invalidDepositSelected'), 'error'); return; }
    if (deposit.payment_details && typeof deposit.payment_details === 'string') {
        try { deposit.payment_details = JSON.parse(deposit.payment_details); } catch { deposit.payment_details = {}; }
    } else if (!deposit.payment_details) { deposit.payment_details = {}; }
    selectedDeposit.value = deposit;
    showModal.value = true;
};

const closeModal = () => { showModal.value = false; setTimeout(() => { selectedDeposit.value = null; }, 300); };

const resetForm = () => {
    selectedGateway.value = null;
    depositForm.value = { amount: null, payment_gateway_id: null };
    paymentDetails.value = {};
    selectedCryptoCurrency.value = 'btc';
};

const goToPage = (url) => {
    if (!url || typeof url !== 'string') return;
    router.get(url, {}, { preserveState: true, preserveScroll: true, onError: () => showToast(t('failedToLoadPage'), 'error') });
};

const copyToClipboard = async (text, label = '') => {
    if (!text) return;
    try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        else { const ta = document.createElement('textarea'); ta.value = text; ta.style.cssText = 'position:fixed;left:-9999px'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
        showToast(label ? `${label} copied!` : t('copied') || 'Copied!', 'success');
    } catch { showToast(t('failedToCopy') || 'Copy failed', 'error'); }
};

const downloadQRCode = async (format = 'png') => {
    try {
        const qrElement = qrCodeWrapper.value?.querySelector('canvas');
        if (!qrElement) { showToast('QR code not ready. Please wait...', 'error'); return; }
        const walletAddress = page.props.auth.user.tron_wallet_address;
        if (!walletAddress) { showToast('Wallet address not found', 'error'); return; }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400; canvas.height = 400;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 400, 400);
        ctx.drawImage(qrElement, 20, 20, 360, 360);
        const link = document.createElement('a');
        link.download = `tron-wallet-qr.${format}`;
        link.href = canvas.toDataURL(`image/${format}`);
        link.click();
        showToast('QR Code downloaded!', 'success');
    } catch { showToast('Download failed', 'error'); }
};

const verifyTronTransaction = async (deposit) => {
    if (!tronTxHash.value || tronTxHash.value.length !== 64) { showToast('Please enter a valid transaction hash (64 characters)', 'error'); return; }
    isVerifyingTron.value = true;
    try {
        router.post(`/user/wallet/deposit/${deposit.id}/verify-tron`, { tx_hash: tronTxHash.value }, {
            onSuccess: () => { showToast('Transaction verified!', 'success'); tronTxHash.value = ''; closeModal(); },
            onError: (errors) => { showToast(Object.values(errors).flat()[0] || 'Verification failed', 'error'); },
            preserveScroll: true
        });
    } finally { isVerifyingTron.value = false; }
};

const handleKeydown = (e) => { if (e.key === 'Escape' && showModal.value) closeModal(); };
onMounted(() => { document.addEventListener('keydown', handleKeydown); });
onUnmounted(() => { document.removeEventListener('keydown', handleKeydown); });
</script>

<style scoped>
/* ── Root ── */
.dc { display: flex; flex-direction: column; gap: 20px; }

/* ── Stats ── */
.dc-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
@media (max-width: 768px) { .dc-stats { grid-template-columns: repeat(2, 1fr); } }

.dc-stat-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 16px; padding: 18px 16px;
    position: relative; overflow: hidden;
    transition: transform 0.25s, box-shadow 0.25s;
}
.dc-stat-card::before {
    content: ''; position: absolute;
    top: 0; left: 15%; right: 15%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), transparent);
    pointer-events: none;
}
.dc-stat-card:hover { transform: translateY(-3px); box-shadow: 0 10px 32px rgba(0,0,0,0.4); }
.dc-stat-icon { width: 38px; height: 38px; border-radius: 10px; border: 1px solid; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; transition: transform 0.25s; }
.dc-stat-card:hover .dc-stat-icon { transform: scale(1.1); }
.dc-stat-lbl { font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55); text-transform: uppercase; letter-spacing: 0.08em; margin: 0 0 6px; }
.dc-stat-val { font-size: 22px; font-weight: 900; color: #fff; margin: 0 0 5px; line-height: 1; }
.dc-stat-sub { font-size: 11px; font-weight: 700; }

/* ── Main layout ── */
.dc-main { display: grid; grid-template-columns: 1fr 320px; gap: 20px; }
.dc-left { display: flex; flex-direction: column; }
.dc-right { display: flex; flex-direction: column; gap: 16px; }
@media (max-width: 1100px) { .dc-main { grid-template-columns: 1fr 280px; } }
@media (max-width: 900px) { .dc-main { grid-template-columns: 1fr; } .dc-right { display: none; } }

/* ── Card ── */
.dc-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px; padding: 24px;
    position: relative; overflow: hidden;
}
.dc-card::before {
    content: ''; position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.2), transparent);
    pointer-events: none;
}
.dc-card-head { margin-bottom: 20px; }
.dc-card-title { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 4px; }
.dc-card-sub { font-size: 12px; color: rgba(148,163,184,0.5); margin: 0; }
.dc-center { text-align: center; }

/* ── Gateways ── */
.dc-gateways { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
@media (max-width: 480px) { .dc-gateways { grid-template-columns: 1fr; } }
.dc-gateway {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 14px;
    cursor: pointer; transition: all 0.2s;
}
.dc-gateway:hover:not(.dc-gateway--on) { border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); }
.dc-gateway-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dc-gateway-abbr { font-weight: 900; font-size: 13px; color: #0f172a; }
.dc-gateway-info { flex: 1; min-width: 0; }
.dc-gateway-name { font-size: 13px; font-weight: 700; color: #fff; margin: 0 0 4px; }
.dc-gateway-fee { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 50px; }
.dc-gateway-check { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* ── Credentials ── */
.dc-credentials { padding: 16px; border-radius: 13px; border: 1px solid; margin-bottom: 20px; }
.dc-cred-title { font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 14px; }
.dc-cred-img-wrap { display: flex; justify-content: center; margin-bottom: 14px; }
.dc-cred-img-box { background: #fff; padding: 12px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
.dc-cred-img { max-width: 220px; max-height: 220px; border-radius: 8px; }
.dc-cred-rows { display: flex; flex-direction: column; gap: 10px; }
.dc-cred-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); }
.dc-cred-row-info { flex: 1; min-width: 0; }
.dc-cred-row-key { font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.5); text-transform: capitalize; margin: 0 0 4px; }
.dc-cred-row-val { font-size: 13px; font-family: monospace; font-weight: 600; color: #fff; word-break: break-all; margin: 0; }
.dc-copy-btn { padding: 8px; border-radius: 8px; border: 1px solid; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; }
.dc-copy-btn:hover { transform: scale(1.08); filter: brightness(1.2); }

/* ── TRON section ── */
.dc-tron { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 20px; margin-bottom: 4px; display: flex; flex-direction: column; gap: 16px; }
.dc-tron-head { display: flex; align-items: flex-start; gap: 14px; }
.dc-tron-head-icon { width: 46px; height: 46px; border-radius: 13px; background: linear-gradient(135deg,rgba(96,165,250,0.2),rgba(147,51,234,0.2)); border: 1px solid rgba(96,165,250,0.3); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dc-tron-title { font-size: 14px; font-weight: 800; color: #fff; margin: 0 0 8px; }
.dc-tron-badges { display: flex; gap: 6px; flex-wrap: wrap; }
.dc-tron-badge { font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 999px; letter-spacing: 0.05em; }
.dc-tron-badge--green { background: rgba(16,185,129,0.15); border: 1px solid rgba(16,185,129,0.3); color: #6ee7b7; }
.dc-tron-badge--blue { background: rgba(96,165,250,0.15); border: 1px solid rgba(96,165,250,0.3); color: #93c5fd; }
.dc-tron-notice { padding: 13px 16px; border-radius: 11px; background: rgba(96,165,250,0.05); border-left: 3px solid rgba(96,165,250,0.5); }
.dc-tron-notice-txt { font-size: 12px; color: rgba(226,232,240,0.8); margin: 0 0 10px; line-height: 1.5; }
.dc-tron-warn { display: flex; align-items: flex-start; gap: 8px; padding-top: 10px; border-top: 1px solid rgba(96,165,250,0.12); font-size: 11.5px; color: rgba(253,230,138,0.75); line-height: 1.45; }
.dc-tron-grid { display: grid; grid-template-columns: 1fr 180px; gap: 14px; align-items: start; }
@media (max-width: 640px) { .dc-tron-grid { grid-template-columns: 1fr; } }
.dc-tron-addr-box { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.dc-tron-addr-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.dc-tron-addr-lbl { font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55); text-transform: uppercase; letter-spacing: 0.08em; }
.dc-tron-active-badge { font-size: 9px; font-weight: 800; padding: 2px 7px; border-radius: 999px; background: rgba(16,185,129,0.18); border: 1px solid rgba(16,185,129,0.3); color: #6ee7b7; }
.dc-tron-addr { display: block; font-family: monospace; font-size: 12px; font-weight: 600; color: #fff; background: rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.08); border-radius: 9px; padding: 10px 12px; word-break: break-all; line-height: 1.5; }
.dc-tron-btns { display: flex; gap: 8px; }
.dc-tron-btn-copy { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 14px; border-radius: 10px; border: 1px solid; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
.dc-tron-btn-copy:hover { transform: translateY(-1px); filter: brightness(1.1); }
.dc-tron-qr-box { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 14px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.dc-tron-qr-lbl { font-size: 10.5px; font-weight: 700; color: rgba(148,163,184,0.6); text-transform: uppercase; letter-spacing: 0.07em; }
.dc-tron-qr-wrap { }
.dc-tron-qr-inner { background: #fff; padding: 10px; border-radius: 12px; box-shadow: 0 0 20px rgba(167,93,237,0.15); }
.dc-tron-dl-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 700; color: rgba(148,163,184,0.7); background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 5px 12px; cursor: pointer; transition: all 0.2s; }
.dc-tron-dl-btn:hover { color: #fff; background: rgba(255,255,255,0.08); }
.dc-tron-features { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 640px) { .dc-tron-features { grid-template-columns: 1fr; } }
.dc-tron-feat { display: flex; align-items: center; gap: 10px; padding: 11px 13px; border-radius: 11px; border: 1px solid; }
.dc-tron-feat--green { background: rgba(16,185,129,0.05); border-color: rgba(16,185,129,0.2); }
.dc-tron-feat--blue { background: rgba(96,165,250,0.05); border-color: rgba(96,165,250,0.2); }
.dc-tron-feat--purple { background: rgba(167,93,237,0.05); border-color: rgba(167,93,237,0.2); }
.dc-tron-feat--amber { background: rgba(251,191,36,0.05); border-color: rgba(251,191,36,0.2); }
.dc-tron-feat-ic { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dc-tron-feat-title { font-size: 12px; font-weight: 800; margin: 0 0 2px; }
.dc-tron-feat-sub { font-size: 10px; color: rgba(148,163,184,0.5); margin: 0; }
.dc-tron-ready { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border-radius: 11px; background: rgba(16,185,129,0.06); border: 1px solid rgba(16,185,129,0.2); }
.dc-tron-ready-title { font-size: 13px; font-weight: 800; color: #6ee7b7; margin: 0 0 4px; }
.dc-tron-ready-sub { font-size: 11.5px; color: rgba(167,243,208,0.7); margin: 0; line-height: 1.5; }

/* ── NowPayments crypto grid ── */
.dc-crypto-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
@media (max-width: 640px) { .dc-crypto-grid { grid-template-columns: repeat(4, 1fr); } }
.dc-crypto { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 10px 6px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 11px; cursor: pointer; transition: all 0.18s; }
.dc-crypto:hover:not(.dc-crypto--on) { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.14); }
.dc-crypto-sym { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 900; background: rgba(51,65,85,0.5); color: #e2e8f0; transition: all 0.18s; }
.dc-crypto-code { font-size: 9px; font-weight: 800; color: rgba(148,163,184,0.7); text-transform: uppercase; letter-spacing: 0.05em; }

/* ── Form ── */
.dc-form-wrap { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 20px; display: flex; flex-direction: column; gap: 16px; }
.dc-field { display: flex; flex-direction: column; gap: 7px; }
.dc-field-lbl { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }
.dc-field-hint { font-size: 11px; color: rgba(148,163,184,0.45); margin: 0; }
.dc-required { color: #f87171; margin-left: 3px; }
.dc-input {
    width: 100%; padding: 11px 14px; border-radius: 11px;
    border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04);
    color: #fff; font-size: 13.5px; font-weight: 500; outline: none;
    transition: border-color 0.18s; box-sizing: border-box;
}
.dc-input:focus { border-color: rgba(167,93,237,0.45); background: rgba(167,93,237,0.03); }
.dc-input::placeholder { color: rgba(148,163,184,0.35); }
.dc-select { cursor: pointer; }
.dc-select option { background: #0a0416; color: #fff; }
.dc-file-input { width: 100%; font-size: 12.5px; color: rgba(148,163,184,0.7); }
.dc-params { display: flex; flex-direction: column; gap: 14px; }
.dc-params-title { font-size: 13px; font-weight: 800; color: #fff; margin: 0; }
.dc-submit {
    width: 100%; padding: 13px 20px; border-radius: 12px; border: none;
    font-size: 14px; font-weight: 800; cursor: pointer;
    transition: all 0.2s; letter-spacing: 0.02em;
    display: flex; align-items: center; justify-content: center; gap: 8px;
}
.dc-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.08); }
.dc-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.dc-spin { animation: dcSpin 1s linear infinite; flex-shrink: 0; }
@keyframes dcSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ── Summary (right sidebar) ── */
.dc-summary { }
.dc-summary-rows { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.dc-sum-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.dc-sum-lbl { font-size: 12.5px; color: rgba(148,163,184,0.6); font-weight: 600; }
.dc-sum-val { font-size: 13px; font-weight: 700; color: #fff; }
.dc-sum-total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
.dc-sum-lbl--b { font-size: 13px; font-weight: 800; color: #fff; }
.dc-sum-total-val { font-size: 20px; font-weight: 900; }
.dc-receive { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-radius: 10px; border: 1px solid; }
.dc-receive-lbl { font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 600; }
.dc-receive-val { font-size: 17px; font-weight: 900; }

/* ── Gateway logo ── */
.dc-gw-logo { display: flex; justify-content: center; margin-bottom: 14px; }
.dc-gw-logo-img { width: 72px; height: 72px; object-fit: contain; border-radius: 14px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); padding: 10px; }
.dc-gw-desc { font-size: 12px; color: rgba(148,163,184,0.7); line-height: 1.55; padding: 12px 14px; border-radius: 10px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); margin-bottom: 14px; }
.dc-details-list { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
.dc-detail-row { display: flex; justify-content: space-between; align-items: center; }
.dc-detail-lbl { font-size: 12px; color: rgba(148,163,184,0.55); font-weight: 600; }
.dc-detail-val { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }

/* ── Tips ── */
.dc-tips { display: flex; flex-direction: column; gap: 13px; margin-top: 14px; }
.dc-tip { display: flex; align-items: flex-start; gap: 10px; }
.dc-tip-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
.dc-tip-title { font-size: 12.5px; font-weight: 700; color: #fff; margin: 0 0 3px; }
.dc-tip-sub { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; line-height: 1.4; }

/* ── History card ── */
.dc-hist-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px; overflow: hidden; position: relative;
}
.dc-hist-card::before {
    content: ''; position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.2), transparent);
    pointer-events: none;
}
.dc-hist-head {
    display: flex; align-items: center; gap: 14px;
    padding: 20px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap;
}
.dc-hist-head-icon { width: 36px; height: 36px; border-radius: 10px; border: 1px solid; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dc-hist-title { font-size: 14.5px; font-weight: 800; color: #fff; margin: 0 0 3px; }
.dc-hist-sub { font-size: 11.5px; color: rgba(148,163,184,0.55); margin: 0; }
.dc-hist-chip { margin-left: auto; display: flex; align-items: center; gap: 6px; padding: 6px 13px; border-radius: 50px; border: 1px solid; font-size: 11px; font-weight: 700; color: rgba(148,163,184,0.55); }
.dc-hist-chip span:first-child { font-size: 14px; font-weight: 900; }

/* ── Empty ── */
.dc-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 56px 24px; text-align: center; }
.dc-empty-ic { width: 64px; height: 64px; border-radius: 18px; border: 1px solid; display: flex; align-items: center; justify-content: center; }
.dc-empty-title { font-size: 15px; font-weight: 700; color: #fff; margin: 4px 0 0; }
.dc-empty-sub { font-size: 12.5px; color: rgba(148,163,184,0.5); margin: 0; }

/* ── Mobile rows ── */
.dc-mob { display: flex; flex-direction: column; }
@media (min-width: 1024px) { .dc-mob { display: none !important; } }
.dc-row {
    display: grid; grid-template-columns: 1fr auto; grid-template-rows: auto auto;
    gap: 6px 12px; padding: 14px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    border-left: 3px solid transparent;
    transition: all 0.18s; align-items: center;
}
.dc-row:hover { background: rgba(167,93,237,0.04); border-left-color: rgba(167,93,237,0.5); }
.dc-row:last-child { border-bottom: none; }
.dc-row-l { display: flex; align-items: center; gap: 10px; min-width: 0; grid-column: 1; grid-row: 1; }
.dc-row-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.dc-row-trx { font-size: 12.5px; font-weight: 700; color: #fff; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dc-row-date { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; }
.dc-row-r { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; grid-column: 2; grid-row: 1; }
.dc-row-amount { font-size: 13px; font-weight: 800; margin: 0; }
.dc-row-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 50px; border: 1px solid; }
.dc-row-actions { grid-column: 1 / -1; grid-row: 2; display: flex; gap: 8px; }
.dc-view-btn { flex: 1; font-size: 11.5px; font-weight: 700; padding: 6px 14px; border-radius: 8px; border: 1px solid; background: transparent; cursor: pointer; transition: all 0.18s; }

/* ── Desktop table ── */
.dc-tbl-wrap { display: none; overflow-x: auto; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
@media (min-width: 1024px) { .dc-tbl-wrap { display: block; } }
.dc-tbl { width: 100%; border-collapse: collapse; min-width: 900px; }
.dc-tbl-hdr { border-bottom: 1px solid rgba(255,255,255,0.06); }
.dc-th { padding: 11px 16px; text-align: left; font-size: 10.5px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: rgba(148,163,184,0.5); white-space: nowrap; }
.dc-tr { border-bottom: 1px solid rgba(255,255,255,0.04); transition: background 0.15s; }
.dc-tr:last-child { border-bottom: none; }
.dc-tr:hover { background: rgba(167,93,237,0.04); }
.dc-td { padding: 13px 16px; vertical-align: middle; }
.dc-mono { font-family: monospace; font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.9); }
.dc-td-val { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.9); display: block; }
.dc-td-fee { font-size: 11px; font-weight: 600; color: #fbbf24; display: block; }
.dc-td-muted { font-size: 12.5px; color: rgba(148,163,184,0.65); display: block; }
.dc-td-date { font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.75); margin: 0 0 2px; }
.dc-td-time { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; }
.dc-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 50px; border: 1px solid; white-space: nowrap; }
.dc-badge-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex-shrink: 0; animation: dcBlink 2s ease-in-out infinite; }
.dc-badge-dot--pulse { animation: dcBlink 2s ease-in-out infinite; }
@keyframes dcBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.dc-tbl-view { width: 30px; height: 30px; border-radius: 8px; border: 1px solid; display: inline-flex; align-items: center; justify-content: center; background: transparent; cursor: pointer; transition: all 0.18s; }
.dc-tbl-view:hover { transform: scale(1.1); filter: brightness(1.2); }

/* ── Pagination ── */
.dc-pagi { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.06); }
.dc-pagi-info { font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 500; }
.dc-pagi-btns { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.dc-pagi-btn { min-width: 32px; height: 32px; padding: 0 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.7); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.18s; display: inline-flex; align-items: center; justify-content: center; }
.dc-pagi-btn:hover:not(.dc-pagi-btn--on):not(.dc-pagi-btn--off) { background: rgba(255,255,255,0.08); color: #fff; }
.dc-pagi-btn--on { border-color: transparent; font-weight: 800; color: #0f172a; }
.dc-pagi-btn--off { opacity: 0.3; cursor: not-allowed; }

/* ── Modal ── */
.dc-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.72); backdrop-filter: blur(6px); z-index: 50; display: flex; align-items: center; justify-content: center; padding: 16px; }
.dc-modal {
    background: rgba(10,4,22,0.98);
    border: 1px solid rgba(167,93,237,0.2);
    border-radius: 22px; box-shadow: 0 24px 80px rgba(0,0,0,0.6);
    width: 100%; max-width: 680px; max-height: 90vh; overflow-y: auto; position: relative;
}
.dc-modal::before {
    content: ''; position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.5), rgba(232,121,249,0.3), transparent);
    pointer-events: none;
}
.dc-modal-head { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); position: sticky; top: 0; background: rgba(10,4,22,0.98); z-index: 2; }
.dc-modal-title { font-size: 17px; font-weight: 900; color: #fff; margin: 0; }
.dc-modal-close { width: 32px; height: 32px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.7); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.18s; }
.dc-modal-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.dc-modal-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }
.dc-modal-foot { display: flex; align-items: center; padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(5,2,15,0.5); position: sticky; bottom: 0; }
.dc-modal-hcard { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 16px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; }
.dc-modal-hcard-l { display: flex; align-items: center; gap: 12px; min-width: 0; }
.dc-modal-hcard-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dc-modal-plan { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 3px; }
.dc-modal-plan-sub { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; }
.dc-modal-trx { font-family: monospace; font-size: 12.5px; font-weight: 700; padding: 10px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); }
.dc-modal-section { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.dc-modal-section-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; margin: 0; }
.dc-modal-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 540px) { .dc-modal-grid2 { grid-template-columns: 1fr; } }
.dc-modal-field { display: flex; flex-direction: column; gap: 4px; }
.dc-modal-field-lbl { font-size: 10.5px; color: rgba(148,163,184,0.5); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
.dc-modal-field-val { font-size: 13px; font-weight: 700; color: #fff; }
.dc-modal-amounts { display: flex; flex-direction: column; gap: 10px; }
.dc-modal-amount-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.dc-modal-amount-lbl { font-size: 12.5px; color: rgba(148,163,184,0.6); font-weight: 600; }
.dc-modal-amount-val { font-size: 13.5px; font-weight: 700; color: rgba(226,232,240,0.9); }
.dc-modal-amount-total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
.dc-modal-amount-lbl--b { font-size: 13.5px; font-weight: 800; color: #fff; }
.dc-modal-total-val { font-size: 22px; font-weight: 900; }
.dc-modal-note { font-size: 13px; color: rgba(148,163,184,0.7); line-height: 1.55; margin: 0; }
.dc-tron-addr-modal { }
.dc-pay-link { display: inline-flex; align-items: center; gap: 5px; padding: 8px 14px; border-radius: 9px; font-size: 12.5px; font-weight: 700; color: #fff; text-decoration: none; cursor: pointer; border: none; }
.dc-copy-addr-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 9px; font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); cursor: pointer; transition: all 0.18s; }
.dc-copy-addr-btn:hover { background: rgba(255,255,255,0.09); }
.dc-close-btn { padding: 10px 20px; border-radius: 11px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.05); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.18s; }
.dc-close-btn:hover { background: rgba(255,255,255,0.1); }

button:focus-visible { outline: 2px solid rgba(167,93,237,0.6); outline-offset: 2px; }

/* ── BSC USDT BEP-20 wrapper ── */
.dc-bsc {
    margin: 14px 0 0;
    padding: 16px;
    border-radius: 13px;
    background: rgba(240,185,11,0.03);
    border: 1px solid rgba(240,185,11,0.15);
}
</style>
